import dayjs from 'dayjs'

import { Button } from '@/components/button/button'
import { Card, CardContent } from '@/components/card/card'
import { DatePicker } from '@/components/date-picker/date-picker'
import { Form, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Input } from '@/components/input/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/select/select'

import { type ResCaseAlertItem } from '@/lib/queries/business/v1/alert/types'
import { cn } from '@/lib/utils'

import { DeleteConfirmDialog } from './delete-confirm-dialog'
import { useEditAlertCard } from './use-edit-alert-card'

export interface EditAlertCardProps {
  alertData: ResCaseAlertItem
  closeDialog: VoidFunction
}

export const EditAlertCard = ({ alertData, closeDialog }: EditAlertCardProps) => {
  const {
    form,
    formErrors,
    isDisabledSaveButton,
    isLoadingSaveButton,
    isDisabledDeleteButton,
    isLoadingDeleteButton,
    isDeleteConfirmDialogOpen,
    markAlertTypeError,
    markAlertDateError,
    markCountError,
    markUnitError,
    openDeleteConfirmDialog,
    closeDeleteConfirmDialog,
    handleClickSaveButton,
    handleClickDeleteButton,
  } = useEditAlertCard({ alertData, closeDialog })

  return (
    <>
      <Card>
        <CardContent className={cn('p-6')}>
          <Form {...form}>
            <div className={cn('space-y-4')}>
              {/* Alert Type */}
              <FormField
                control={form.control}
                name="alertType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alert Type</FormLabel>
                    <Input
                      {...field}
                      onChange={(e) => {
                        field.onChange(e)
                        markAlertTypeError(e.target.value)
                      }}
                    />
                  </FormItem>
                )}
              />

              {/* Alert Target Date */}
              <FormField
                control={form.control}
                name="alertDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <DatePicker
                      placeholder="MM-DD-YYYY"
                      disabledDateRule={{
                        before: dayjs(new Date()).add(1, 'day').toDate(),
                      }}
                      value={field.value}
                      onSelect={(date) => {
                        field.onChange(date)
                        markAlertDateError(date)
                      }}
                    />
                  </FormItem>
                )}
              />

              {/* Notification Settings */}
              <FormField
                control={form.control}
                name="notificationSettings"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Settings</FormLabel>
                    <div className={cn('flex items-center space-x-2')}>
                      <span className={cn('text-sm font-medium')}>Before</span>
                      <Input
                        type="text"
                        className={cn('w-[65px]')}
                        value={field.value?.count}
                        onChange={(e) => {
                          form.setValue(field.name, {
                            ...field.value,
                            count: e.target.value,
                          })
                          markCountError(e.target.value)
                        }}
                      />
                      <Select
                        value={field.value?.unit}
                        onValueChange={(value) => {
                          form.setValue(field.name, {
                            ...field.value,
                            unit: value,
                          })
                          markUnitError(value)
                        }}
                      >
                        <SelectTrigger className={cn('w-[90px]')}>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="day" value="day">
                            Day
                          </SelectItem>
                          <SelectItem key="week" value="week">
                            Week
                          </SelectItem>
                          <SelectItem key="month" value="month">
                            Month
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formErrors.notificationSettings?.message && (
                      <p className={cn('text-xs font-medium text-destructive')}>
                        {formErrors.notificationSettings.message}
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Form>
        </CardContent>
      </Card>

      <div className={cn('space-x-2')}>
        <Button
          type="button"
          disabled={isDisabledSaveButton}
          loading={isLoadingSaveButton}
          onClick={handleClickSaveButton}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={isDisabledDeleteButton}
          loading={isLoadingDeleteButton}
          onClick={openDeleteConfirmDialog}
        >
          Delete
        </Button>
      </div>

      {isDeleteConfirmDialogOpen && (
        <DeleteConfirmDialog
          open={isDeleteConfirmDialogOpen}
          onClose={closeDeleteConfirmDialog}
          onClickDeleteButton={handleClickDeleteButton}
        />
      )}
    </>
  )
}
